import { html } from 'lit-html';
import { t } from '../i18n/index.js';

// Global state for notifications
let notificationState = {
  notifications: [],
  idCounter: 0
};

// Timer tracking to prevent memory leaks
const notificationTimers = new Map();

// Cache renderApp reference
let renderAppRef = null;
const triggerRender = async () => {
  if (!renderAppRef) {
    const { renderApp } = await import('../main.js');
    renderAppRef = renderApp;
  }
  renderAppRef();
};

// Maximum notifications to prevent UI overflow
const MAX_NOTIFICATIONS = 5;

/**
 * Add a notification to the queue
 * @param {Object} notification - Notification object
 * @param {string} notification.type - Type: 'success', 'error', 'warning', 'info'
 * @param {string} notification.title - Notification title
 * @param {string} notification.message - Notification message
 * @param {number} notification.duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
 * @param {Array} notification.actions - Optional action buttons [{text, primary, action}]
 */
export const addNotification = ({
  type = 'info',
  title,
  message,
  duration = 5000,
  actions = null
}) => {
  const notification = {
    id: ++notificationState.idCounter,
    type,
    title,
    message,
    duration,
    actions,
    createdAt: Date.now()
  };

  notificationState.notifications.push(notification);

  // Enforce max notifications limit - remove oldest
  while (notificationState.notifications.length > MAX_NOTIFICATIONS) {
    const oldest = notificationState.notifications.shift();
    if (notificationTimers.has(oldest.id)) {
      clearTimeout(notificationTimers.get(oldest.id));
      notificationTimers.delete(oldest.id);
    }
  }

  // Auto-dismiss after duration with timer tracking
  if (duration > 0) {
    const timerId = setTimeout(() => {
      notificationTimers.delete(notification.id);
      removeNotification(notification.id);
    }, duration);
    notificationTimers.set(notification.id, timerId);
  }

  // Trigger re-render
  triggerRender();

  return notification.id;
};

/**
 * Remove a notification by ID
 * @param {number} id - Notification ID
 */
export const removeNotification = (id) => {
  // Clear any pending timer to prevent memory leak
  if (notificationTimers.has(id)) {
    clearTimeout(notificationTimers.get(id));
    notificationTimers.delete(id);
  }

  notificationState.notifications = notificationState.notifications.filter(
    notification => notification.id !== id
  );
  triggerRender();
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = () => {
  // Clear all pending timers
  notificationTimers.forEach((timerId) => clearTimeout(timerId));
  notificationTimers.clear();

  notificationState.notifications = [];
  triggerRender();
};

/**
 * Convenience methods for different notification types
 */
export const showSuccess = (title, message, durationOrActions = 5000) => {
  if (Array.isArray(durationOrActions)) {
    // Notifications with actions should not auto-dismiss
    return addNotification({ type: 'success', title, message, actions: durationOrActions, duration: 0 });
  }
  return addNotification({ type: 'success', title, message, duration: durationOrActions });
};

export const showError = (title, message, durationOrActions = 8000) => {
  if (Array.isArray(durationOrActions)) {
    // Notifications with actions should not auto-dismiss
    return addNotification({ type: 'error', title, message, actions: durationOrActions, duration: 0 });
  }
  return addNotification({ type: 'error', title, message, duration: durationOrActions });
};

export const showWarning = (title, message, duration) => {
  return addNotification({ type: 'warning', title, message, duration });
};

export const showInfo = (title, message, duration) => {
  return addNotification({ type: 'info', title, message, duration });
};

/**
 * Notification container component
 */
export const notificationContainer = () => {
  if (notificationState.notifications.length === 0) {
    return html``;
  }

  return html`
    <div class="notification-container">
      ${notificationState.notifications.map(notification => html`
        <div class="notification notification-${notification.type}" data-id="${notification.id}">
          <div class="notification-content">
            <div class="notification-icon">
              ${getNotificationIcon(notification.type)}
            </div>
            <div class="notification-text">
              ${notification.title ? html`
                <div class="notification-title">${notification.title}</div>
              ` : ''}
              <div class="notification-message">${notification.message}</div>
            </div>
          </div>
          
          <!-- Ações da notificação -->
          ${notification.actions && notification.actions.length > 0 ? html`
            <div class="notification-actions">
              ${notification.actions.map(action => html`
                <button 
                  class="notification-action ${action.primary ? 'primary' : 'secondary'}"
                  @click=${() => {
                    action.action();
                    removeNotification(notification.id);
                  }}
                >
                  ${action.text}
                </button>
              `)}
            </div>
          ` : ''}
          
          <button 
            class="notification-close" 
            @click=${() => removeNotification(notification.id)}
            title="${t('notifications.closeNotification')}"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `)}
    </div>

    <style>
      .notification-container {
        position: fixed;
        top: 24px;
        right: 24px;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
        pointer-events: none;
      }

      .notification {
        background: var(--bg-primary);
        border-radius: 12px;
        padding: 16px;
        box-shadow: var(--shadow-lg);
        border-left: 4px solid;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        pointer-events: auto;
        transform: translateX(100%);
        animation: slideIn 0.3s ease forwards;
        min-width: 320px;
        border: 1px solid var(--border-primary);
      }

      .notification-success {
        border-left-color: var(--success);
      }

      .notification-error {
        border-left-color: var(--error);
      }

      .notification-warning {
        border-left-color: var(--warning);
      }

      .notification-info {
        border-left-color: var(--info);
      }

      .notification-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex: 1;
      }

      .notification-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        margin-top: 2px;
      }

      .notification-success .notification-icon {
        color: var(--success);
      }

      .notification-error .notification-icon {
        color: var(--error);
      }

      .notification-warning .notification-icon {
        color: var(--warning);
      }

      .notification-info .notification-icon {
        color: var(--info);
      }

      .notification-text {
        flex: 1;
      }

      .notification-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 4px;
        line-height: 1.4;
      }

      .notification-message {
        font-size: 14px;
        color: var(--text-secondary);
        line-height: 1.4;
      }

      .notification-close {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }

      .notification-close:hover {
        color: var(--text-tertiary);
        background: var(--bg-tertiary);
      }
      
      .notification-actions {
        display: flex;
        gap: 8px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--border-primary);
      }
      
      .notification-action {
        padding: 6px 12px;
        border-radius: 6px;
        border: none;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        pointer-events: auto;
      }
      
      .notification-action.primary {
        background: var(--brand-primary);
        color: white;
      }
      
      .notification-action.primary:hover {
        background: var(--brand-hover);
      }
      
      .notification-action.secondary {
        background: var(--bg-tertiary);
        color: var(--text-tertiary);
      }
      
      .notification-action.secondary:hover {
        background: var(--border-primary);
        color: var(--text-secondary);
      }

      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .notification-container {
          top: 16px;
          right: 16px;
          left: 16px;
          max-width: none;
        }

        .notification {
          min-width: auto;
        }
      }
    </style>
  `;
};

/**
 * Get icon for notification type
 * @param {string} type - Notification type
 */
const getNotificationIcon = (type) => {
  switch (type) {
    case 'success':
      return html`
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      `;
    case 'error':
      return html`
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      `;
    case 'warning':
      return html`
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <circle cx="12" cy="17" r="0.5"></circle>
        </svg>
      `;
    case 'info':
    default:
      return html`
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <circle cx="12" cy="8" r="0.5"></circle>
        </svg>
      `;
  }
};